/**
 * Yelp Safe Fetch Shim (Story 23.5 AC #4)
 *
 * Thin wrapper around the generic `safeFetch` that ensures BEFORE any
 * logging, error reporting, or Sentry breadcrumb, the decrypted Yelp API
 * Key is replaced with a redacted form:
 *
 *   `Authorization: Bearer <YELP_API_KEY>` → `Authorization: Bearer [redacted-yelp-api-key]`
 *
 * Per Round 3 Red Team Attack #1 + #2: this shim is the last line of
 * defense against decrypted plaintext leaking into logs. The
 * `sanitizeYelpErrorMessage` helper from `./errors` does the actual
 * replacement work — this shim plumbs the decrypted API Key through to the
 * error path.
 *
 * Co-located unit tests in `safe-fetch.test.ts` assert:
 *  - Bearer token redacted in error message
 *  - apiKey redacted in URL-encoded variants
 *  - error message containing the literal API Key plaintext → redacted
 *  - error message with URL-encoded API Key → redacted
 *  - apikey header value redacted across multiple log lines
 */
import type { AdapterResult } from "../adapter";
import { adapterErr } from "../adapter";
import { safeFetch } from "../safe-fetch";

import { sanitizeYelpErrorMessage } from "./errors";

export interface YelpSafeFetchContext {
  readonly decryptedApiKey?: string;
}

/**
 * Wraps `safeFetch` so any error message is run through
 * `sanitizeYelpErrorMessage` BEFORE it propagates to the adapter's error
 * path. This makes the redaction unavoidable — adapter code that forgets
 * to call `sanitizeYelpErrorMessage` manually still gets safe errors.
 */
export async function safeFetchYelp(
  url: string,
  init: RequestInit,
  context: YelpSafeFetchContext = {},
  timeoutMs?: number,
): Promise<AdapterResult<Response>> {
  const result = await safeFetch(url, init, timeoutMs);
  if (result.success) return result;

  return adapterErr({
    ...result.error,
    message: sanitizeYelpErrorMessage(
      result.error.message,
      context.decryptedApiKey,
    ),
    providerMessage: result.error.providerMessage
      ? sanitizeYelpErrorMessage(
          result.error.providerMessage,
          context.decryptedApiKey,
        )
      : undefined,
  });
}
